/* eslint-disable react/prop-types */
import { React, useMemo } from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import PropTypes from 'prop-types';

import { Container, Title } from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import {
  hasNoAccess,
  generateConstant,
  isVn,
  capitalizeText,
} from '@global/helpers';
import BrandLogo from '@/screens/catalog/components/BrandLogo';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetBrandListQuery,
  useGetBrandCountQuery,
  useGetBrandInfiniteListInfiniteQuery,
} from '@api/listApi';

const BrandScreen = (props) => {
  // ── derived ──
  const hasAccess = !hasNoAccess([
    'superadmin',
    'sales_admin',
    'marketing_admin',
    'general',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetBrandListQuery,
    countHook: useGetBrandCountQuery,
    infiniteHook: useGetBrandInfiniteListInfiniteQuery,
  });

  // ── memoized values ──
  const structures = useMemo(() => {
    const isVietnam = isVn();
    const centerHeadStyle = { textAlign: 'center' };
    const cols = {
      name: {
        head: 'Brand Name',
        body: 'name',
        headStyle: centerHeadStyle,
      },
      collaboration_type: {
        head: 'Brand Type',
        body: isVietnam ? 'collaboration_type_vn' : 'collaboration_type',
        headStyle: centerHeadStyle,
      },
      collaboration_status: {
        head: 'Brand Status',
        body: isVietnam ? 'collaboration_status_vn' : 'collaboration_status',
        headStyle: centerHeadStyle,
      },
    };
    if (hasAccess) {
      cols.action = {
        head: 'Action',
        body: '',
        type: 'action_icon',
        actions: [
          { name: 'edit', icon: require('@assets/img/icons/edit-grey.svg') },
        ],
        headStyle: centerHeadStyle,
      };
    }
    return cols;
  }, [hasAccess]);

  // Apply display transforms (capitalize) over raw list data.
  // Web list query returns a flat array; mobile infinite query returns { pages: [...] }.
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map((item) => ({
      ...item,
      collaboration_type: capitalizeText(item.collaboration_type),
      collaboration_status: capitalizeText(item.collaboration_status),
      collaboration_type_vn: capitalizeText(item.collaboration_type_vn),
      collaboration_status_vn: capitalizeText(item.collaboration_status_vn),
    }));
  }, [list.data]);

  // ── handlers ──
  const navigateDetail = (navParams) => {
    openChild('BrandNavigator', {
      screen: 'BrandDetailScreen',
      params: navParams,
    });
  };

  const callBackRowPress = ({ item }) => {
    if (!item || !item._id) {
      return false;
    }
    navigateDetail({
      id: item._id,
      name: item.name,
      prev_page: '',
      tab: '',
    });
  };

  const callBackAction = (data) => {
    if (data.name === 'edit') {
      navigateDetail({
        id: data.data._id,
        name: data.data.name,
        prev_page: '',
        tab: '',
      });
    }
  };

  const renderItem = ({ data, index }) => (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={() => callBackAction({ name: 'edit', data })}
      key={`list-brand-${index}`}
      style={styles.mobileCard}>
      <View style={styles.mobileTopRow}>
        <View style={styles.mobileLeftSection}>
          <View style={styles.mobileLogoWrapper}>
            <BrandLogo url={data?.logo} />
          </View>

          <View style={styles.mobileNameWrapper}>
            <SociollaText type="medium" style={styles.mobileBrandName}>
              {data?.name || '-'}
            </SociollaText>
            <SociollaText style={styles.mobileBrandMeta}>
              {`${data?.collaboration_type || '-'} | ${
                data?.collaboration_status || '-'
              }`}
            </SociollaText>
          </View>
        </View>
      </View>

      <View style={styles.mobileBottomRow}>
        <View style={styles.mobileInfoRow}>
          <SociollaText style={styles.mobileInfoText}>
            {`${data?.created_at ? generateConstant(data.created_at) : '-'}${
              data?.created_by_name ? ` | ${data.created_by_name}` : ''
            }`}
          </SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  // ── render ──
  return (
    <Container>
      {isWeb && <Title title="Brand"></Title>}
      {isWeb ? (
        <Table
          filterName={scope}
          name="brand"
          structures={structures}
          data={rows}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          loading={list.isLoading}
          refetching={list.isFetching}
          showPagination={true}
          showSearch={true}
          searchPlaceholder={'Search by name'}
          searchKey={'name'}
          showFilter={false}
          hasNoHide={true}
          hasCallback={true}
          onClickRow={({ data }) => callBackRowPress({ item: data })}
          onMapAction={({ data, action }) =>
            callBackAction({ name: action.name, data })
          }
        />
      ) : (
        <TableMobile
          filterName={scope}
          data={rows}
          renderItem={renderItem}
          headerComponent={<></>}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          totalData={count.data}
          showSearch={true}
          placeholder={'Search by name'}
          searchKey={'name'}
        />
      )}
    </Container>
  );
};

const styles = StyleSheet.create({
  mobileCard: {
    width: '100%',
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey9,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  mobileTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileLeftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 12,
  },
  mobileLogoWrapper: {
    marginRight: 12,
  },
  mobileNameWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
  mobileBrandName: {
    fontSize: 16,
    color: Colors.black1,
  },
  mobileBrandMeta: {
    fontSize: 12,
    color: Colors.black5,
    marginTop: 4,
  },
  mobileBottomRow: {
    marginTop: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 12,
  },
  mobileInfoText: {
    fontSize: 13,
    color: Colors.black5,
  },
});

BrandScreen.propTypes = {
  children: PropTypes.node,
};

BrandScreen.defaultProps = {
  children: <></>,
};

export default BrandScreen;
